"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/contexts/ToastContext";
import TurnstileWidget from "@/components/ui/TurnstileWidget";

export default function ForgotPasswordPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>();
  const { showToast } = useToast();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    if (!captchaToken) {
      showToast("Please complete the CAPTCHA verification", "error");
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password?email=${encodeURIComponent(email)}`,
        captchaToken,
      });

      if (error) {
        throw error;
      }

      showToast(
        "Password reset instructions have been sent to your email",
        "success",
      );

      // Redirect to reset-password page with email pre-populated
      router.push(`/reset-password?email=${encodeURIComponent(email)}`);
    } catch (error) {
      console.error("Password reset error:", error);
      showToast(
        "Failed to send reset instructions. Please try again.",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Reset your password
          </h1>
          <p className="mt-2 text-sm text-text-light">
            Enter your email address and we&apos;ll send you instructions to
            reset your password.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            label="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <TurnstileWidget onTokenChange={setCaptchaToken} />

          <div className="space-y-4">
            <Button
              type="submit"
              className="w-full"
              size="lg"
              isLoading={isLoading}
              disabled={!email || !captchaToken || isLoading}
              haptic="light"
            >
              Send reset instructions
            </Button>

            <div className="text-center">
              <Link
                href="/login"
                className="text-sm text-primary hover:text-primary-600 font-medium"
              >
                Return to login
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
